import React from "react";

type CellClampProps = {
  cell?: any;
  maxLines?: number;
  minChars?: number;
};

const splitLines = (value: string) => value.split(/\r\n|\n\r|\n|\r/);

export function CellClamp({ cell, maxLines = 3, minChars = 120 }: CellClampProps) {
  if (!cell) return null;

  const row = cell.getRow?.();
  const field = cell.getField?.();

  const tableValue = cell.getValue?.();
  const dataVal = row?.getData?.()?.[field];
  const raw = String(tableValue ?? dataVal ?? "");

  const text = raw.replace(/\r\n|\n\r|\r/g, "\n");
  const lines = splitLines(text);

  const data = row?.getData?.() ?? {};
  const expanded = Boolean(data._expanded?.[field]);

  const onToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!row || !field) return;

    const table = cell.getTable?.();
    const tableEl: HTMLElement | undefined = table?.element;
    const holder = tableEl?.querySelector<HTMLElement>(".tabulator-tableholder");

    // ✅ capture current scroll so redraw/height changes don't move viewport
    const scrollTop = holder?.scrollTop ?? 0;
    const scrollLeft = holder?.scrollLeft ?? 0;

    const current = Boolean(row.getData()._expanded?.[field]);
    const nextExpanded = { ...(row.getData()._expanded || {}), [field]: !current };

    row.update({ _expanded: nextExpanded }).then(() => {
      // ✅ usually enough; avoids aggressive full redraw
      try { row.normalizeHeight(true); } catch {}

      // 🔻 If you *must* redraw, keep it, but restore scroll after it runs
      try { table?.redraw?.(true); } catch {}

      requestAnimationFrame(() => {
        if (holder) {
          holder.scrollTop = scrollTop;
          holder.scrollLeft = scrollLeft;
        }
      });
    });
  };

  return (
    <div>
      <div
        className={expanded ? undefined : "clamp"}
        style={
          expanded
            ? { whiteSpace: "normal" }
            : {
                WebkitLineClamp: String(maxLines),
                whiteSpace: "pre-line",
              }
        }
      >
        {lines.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < lines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </div>

      {text.length > (minChars ?? 120) && (
        <button
          type="button"
          className="tabulator-more-btn"
          onMouseDown={(ev) => ev.preventDefault()} // ✅ prevents focus-scroll jump
          onClick={onToggle}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
